import React, { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { API, useAuth } from "@/App";
import AdminLayout from "@/components/layouts/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  BarChart,
  Bar,
  LineChart,
  Line,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from "recharts";
import {
  MousePointer,
  Users,
  Smartphone,
  Monitor,
  Globe,
  Download,
  FileText,
  FileSpreadsheet,
  Search,
  TrendingUp,
} from "lucide-react";
import { toast } from "sonner";

const RANGE_LABEL = {
  daily: "Last 24h",
  weekly: "Last 7 days",
  monthly: "Last 30 days",
  yearly: "Last 12 months",
};

const TYPE_LABEL = {
  brand_product: "Brand Product",
  affiliate_product: "Affiliate Product",
  financial_product: "Finance Product",
  brand: "Brand",
  seller_product: "Seller Product",
};

const COLORS = ["#0f766e", "#f97316", "#3b82f6", "#a855f7", "#ec4899", "#f59e0b", "#22c55e", "#06b6d4"];

const AdminAnalytics = () => {
  const { getAuthHeaders, token } = useAuth();
  const [overview, setOverview] = useState(null);
  const [clicks, setClicks] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [pageSize] = useState(25);
  const [loading, setLoading] = useState(true);
  const [range, setRange] = useState("weekly");
  const [itemType, setItemType] = useState("all");
  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [exporting, setExporting] = useState(false);

  // Debounce search
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const commonParams = { range };
      if (itemType !== "all") commonParams.item_type = itemType;
      if (debouncedSearch) commonParams.search = debouncedSearch;

      const [ovRes, clRes] = await Promise.all([
        axios.get(`${API}/admin/analytics/overview`, {
          headers: getAuthHeaders(),
          params: { range },
        }),
        axios.get(`${API}/admin/analytics/clicks`, {
          headers: getAuthHeaders(),
          params: { ...commonParams, limit: pageSize, skip: page * pageSize },
        }),
      ]);
      setOverview(ovRes.data);
      setClicks(clRes.data.clicks || []);
      setTotal(clRes.data.total || 0);
    } catch (err) {
      toast.error("Failed to load analytics");
    }
    setLoading(false);
  }, [range, itemType, debouncedSearch, page, pageSize, getAuthHeaders]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  // Reset to page 0 when filters change
  useEffect(() => {
    setPage(0);
  }, [range, itemType, debouncedSearch]);

  const handleExport = async (fmt) => {
    setExporting(true);
    try {
      const params = new URLSearchParams({ format: fmt, range });
      if (itemType !== "all") params.append("item_type", itemType);
      if (debouncedSearch) params.append("search", debouncedSearch);
      const res = await fetch(`${API}/admin/analytics/export?${params.toString()}`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error("Export failed");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      const ts = new Date().toISOString().replace(/[:.]/g, "-").slice(0, 16);
      a.href = url;
      a.download = `analytics_${ts}.${fmt}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`${fmt.toUpperCase()} downloaded`);
    } catch (err) {
      toast.error("Export failed");
    }
    setExporting(false);
  };

  const kpi = (label, value, Icon, color) => (
    <Card className="border-0 shadow-sm">
      <CardContent className="p-4">
        <div className="flex items-center gap-4">
          <div className={`p-3 rounded-xl ${color}`}>
            <Icon className="w-6 h-6" />
          </div>
          <div>
            <p className="text-2xl font-bold" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, "-")}`}>
              {value}
            </p>
            <p className="text-sm text-gray-500">{label}</p>
          </div>
        </div>
      </CardContent>
    </Card>
  );

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <AdminLayout title="Analytics">
      {/* Filters row */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-4">
        <p className="text-gray-500">
          Track clicks, devices, and top brands. {RANGE_LABEL[range]}.
        </p>
        <div className="flex items-center gap-3 flex-wrap">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search item, brand, user..."
              className="pl-9 w-56"
              data-testid="analytics-search-input"
            />
          </div>
          <Select value={itemType} onValueChange={setItemType}>
            <SelectTrigger className="w-44" data-testid="analytics-type-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              <SelectItem value="brand_product">Brand Products</SelectItem>
              <SelectItem value="brand">Brands</SelectItem>
              <SelectItem value="affiliate_product">Affiliate Products</SelectItem>
              <SelectItem value="financial_product">Finance Products</SelectItem>
              <SelectItem value="seller_product">Seller Products</SelectItem>
            </SelectContent>
          </Select>
          <Select value={range} onValueChange={setRange}>
            <SelectTrigger className="w-32" data-testid="analytics-range-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="daily">Last 24h</SelectItem>
              <SelectItem value="weekly">Last 7 days</SelectItem>
              <SelectItem value="monthly">Last 30 days</SelectItem>
              <SelectItem value="yearly">Last 12 months</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Export bar */}
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3 p-4 bg-gradient-to-r from-primary-50 to-secondary-50 border border-primary-100 rounded-xl">
        <div className="flex items-center gap-2 text-sm text-primary-700">
          <Download className="w-4 h-4" />
          <span className="font-medium">Export filtered data:</span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            variant="outline"
            disabled={exporting}
            onClick={() => handleExport("csv")}
            data-testid="export-csv-btn"
          >
            <FileText className="w-4 h-4 mr-1" />
            CSV
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={exporting}
            onClick={() => handleExport("xlsx")}
            data-testid="export-xlsx-btn"
          >
            <FileSpreadsheet className="w-4 h-4 mr-1" />
            Excel
          </Button>
          <Button
            size="sm"
            variant="outline"
            disabled={exporting}
            onClick={() => handleExport("pdf")}
            data-testid="export-pdf-btn"
          >
            <FileText className="w-4 h-4 mr-1" />
            PDF
          </Button>
        </div>
      </div>

      {loading && !overview ? (
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded-xl animate-pulse" />
          ))}
        </div>
      ) : overview ? (
        <>
          {/* KPI Row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            {kpi("Total Clicks", overview.total_clicks, MousePointer, "bg-blue-100 text-blue-600")}
            {kpi("Unique Users", overview.unique_users, Users, "bg-purple-100 text-purple-600")}
            {kpi(
              "Top Device",
              overview.clicks_by_device?.[0]?.device || "—",
              Smartphone,
              "bg-fuchsia-100 text-fuchsia-600",
            )}
            {kpi(
              "Top Brand",
              overview.top_brands?.[0]?.brand_name || "—",
              TrendingUp,
              "bg-amber-100 text-amber-600",
            )}
          </div>

          {/* Charts Row */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
            {/* Trend line */}
            <Card className="border-0 shadow-sm lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <TrendingUp className="w-4 h-4 text-primary-500" />
                  Clicks Over Time
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {overview.clicks_by_day?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <LineChart data={overview.clicks_by_day}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis dataKey="day" tick={{ fontSize: 11 }} />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip />
                        <Line
                          type="monotone"
                          dataKey="count"
                          stroke="#0f766e"
                          strokeWidth={2}
                          dot={{ r: 3 }}
                        />
                      </LineChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      No data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Device Pie */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Smartphone className="w-4 h-4 text-fuchsia-500" />
                  Device Breakdown
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-64">
                  {overview.clicks_by_device?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={overview.clicks_by_device}
                          dataKey="count"
                          nameKey="device"
                          innerRadius={40}
                          outerRadius={80}
                          paddingAngle={2}
                          label={(e) => `${e.device}`}
                        >
                          {overview.clicks_by_device.map((_, i) => (
                            <Cell key={i} fill={COLORS[i % COLORS.length]} />
                          ))}
                        </Pie>
                        <Tooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      No data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Second charts row */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            {/* Browser */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Globe className="w-4 h-4 text-blue-500" />
                  Top Browsers
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  {overview.clicks_by_browser?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={overview.clicks_by_browser} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis type="number" tick={{ fontSize: 11 }} />
                        <YAxis
                          type="category"
                          dataKey="browser"
                          width={110}
                          tick={{ fontSize: 11 }}
                        />
                        <Tooltip />
                        <Bar dataKey="count" fill="#3b82f6" radius={[0, 4, 4, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      No data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Clicks by item type */}
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base flex items-center gap-2">
                  <Monitor className="w-4 h-4 text-purple-500" />
                  Clicks by Type
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="h-56">
                  {overview.clicks_by_type?.length > 0 ? (
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={overview.clicks_by_type}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                        <XAxis
                          dataKey="type"
                          tick={{ fontSize: 10 }}
                          tickFormatter={(v) => TYPE_LABEL[v] || v}
                        />
                        <YAxis tick={{ fontSize: 11 }} />
                        <Tooltip formatter={(v) => v} labelFormatter={(v) => TYPE_LABEL[v] || v} />
                        <Bar dataKey="count" fill="#a855f7" radius={[4, 4, 0, 0]} />
                      </BarChart>
                    </ResponsiveContainer>
                  ) : (
                    <div className="h-full flex items-center justify-center text-gray-400 text-sm">
                      No data
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Top Brands & Top Items */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Top Brands</CardTitle>
              </CardHeader>
              <CardContent>
                {overview.top_brands?.length > 0 ? (
                  <div className="space-y-2">
                    {overview.top_brands.map((b, i) => (
                      <div
                        key={b.brand_id || i}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3">
                          <span className="w-6 text-xs text-gray-400">#{i + 1}</span>
                          <span className="font-medium text-sm">{b.brand_name}</span>
                        </div>
                        <Badge className="bg-blue-100 text-blue-700">{b.count} clicks</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8 text-sm">No brand clicks yet</p>
                )}
              </CardContent>
            </Card>

            <Card className="border-0 shadow-sm">
              <CardHeader>
                <CardTitle className="text-base">Top Items</CardTitle>
              </CardHeader>
              <CardContent>
                {overview.top_items?.length > 0 ? (
                  <div className="space-y-2">
                    {overview.top_items.map((it, i) => (
                      <div
                        key={it.item_id || i}
                        className="flex items-center justify-between p-2 rounded-lg hover:bg-gray-50"
                      >
                        <div className="flex items-center gap-3 min-w-0">
                          <span className="w-6 text-xs text-gray-400">#{i + 1}</span>
                          <div className="min-w-0">
                            <p className="font-medium text-sm truncate">{it.item_name}</p>
                            <p className="text-xs text-gray-400">{TYPE_LABEL[it.item_type] || it.item_type}</p>
                          </div>
                        </div>
                        <Badge className="bg-fuchsia-100 text-fuchsia-700 flex-shrink-0">
                          {it.count}
                        </Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-center text-gray-400 py-8 text-sm">No item clicks yet</p>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Detailed clicks table */}
          <Card className="border-0 shadow-sm">
            <CardHeader className="flex-row items-center justify-between space-y-0">
              <CardTitle className="text-base">Recent Clicks</CardTitle>
              <p className="text-sm text-gray-500">
                Showing {clicks.length} of {total.toLocaleString()}
              </p>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Time</TableHead>
                      <TableHead>User</TableHead>
                      <TableHead>Item</TableHead>
                      <TableHead>Brand</TableHead>
                      <TableHead>Type</TableHead>
                      <TableHead>Device</TableHead>
                      <TableHead>Browser</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {clicks.map((c, i) => (
                      <TableRow key={`${c.created_at}-${i}`}>
                        <TableCell className="text-xs text-gray-500 whitespace-nowrap">
                          {new Date(c.created_at).toLocaleString()}
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">
                            <p className="font-medium">{c.user_name || "Guest"}</p>
                            <p className="text-xs text-gray-400">{c.user_mobile || "—"}</p>
                          </div>
                        </TableCell>
                        <TableCell className="max-w-[200px]">
                          <p className="text-sm truncate">{c.item_name || "—"}</p>
                        </TableCell>
                        <TableCell className="text-sm">{c.brand_name || "—"}</TableCell>
                        <TableCell>
                          <Badge className="bg-gray-100 text-gray-700 text-xs">
                            {TYPE_LABEL[c.item_type] || c.item_type}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-sm capitalize">{c.device_type || "—"}</TableCell>
                        <TableCell className="text-sm">{c.browser || "—"}</TableCell>
                      </TableRow>
                    ))}
                    {clicks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={7} className="text-center text-gray-400 py-8">
                          No clicks match your filters
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between mt-4">
                  <p className="text-sm text-gray-500">
                    Page {page + 1} of {totalPages}
                  </p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page === 0}
                      onClick={() => setPage(page - 1)}
                      data-testid="analytics-prev-page"
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={page + 1 >= totalPages}
                      onClick={() => setPage(page + 1)}
                      data-testid="analytics-next-page"
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </>
      ) : (
        <Card className="border-0 shadow-sm">
          <CardContent className="p-12 text-center">
            <MousePointer className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">No Analytics Data</h3>
            <p className="text-gray-500">Click data will appear once users interact with products</p>
          </CardContent>
        </Card>
      )}
    </AdminLayout>
  );
};

export default AdminAnalytics;
